"use client";

/**
 * Preview Banner Component
 *
 * Displays a fixed banner when the site is in preview/draft mode.
 * Shows "Preview Mode" indicator with an exit link.
 *
 * Client component to access window.location for current path.
 *
 * Following CLAUDE.md:
 * - Brand-agnostic styling (uses Tailwind tokens)
 * - No CMS SDK imports
 */

import Link from "next/link";

export interface PreviewBannerProps {
	/** Current page path for redirect after exiting preview */
	currentPath?: string;
}

export function PreviewBanner({ currentPath }: PreviewBannerProps) {
	// Use current browser path if not provided (for staying on same page after exit)
	const path =
		currentPath ||
		(typeof window !== "undefined" ? window.location.pathname : "/");
	const exitUrl = `/api/exit-preview?path=${encodeURIComponent(path)}`;

	return (
		<div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 py-2 px-4 text-center text-sm font-medium shadow-lg">
			<span className="inline-flex items-center gap-2">
				<span className="inline-block w-2 h-2 rounded-full bg-amber-950 animate-pulse" />
				<span>Preview Mode</span>
				<span className="mx-1">—</span>
				<span className="hidden sm:inline">You are viewing draft content</span>
			</span>
			<Link
				href={exitUrl}
				className="ml-4 underline hover:no-underline font-semibold"
				prefetch={false}
			>
				Exit Preview
			</Link>
		</div>
	);
}
